export const SESSION_REFRESH_LOCK = 'vello.session.refresh';
function runtimeLocks() {
    try {
        return globalThis.navigator?.locks;
    }
    catch {
        return undefined;
    }
}
/**
 * Create the lock. `locks` defaults to the runtime `navigator.locks`; pass `null` to force the in-process fallback
 * (serializes only within this JS context).
 */
export function createCrossContextLock(options = {}) {
    const locks = options.locks === undefined ? runtimeLocks() : (options.locks ?? undefined);
    if (locks !== undefined) {
        return { runExclusive: (name, task) => locks.request(name, { mode: 'exclusive' }, () => task()) };
    }
    const tails = new Map();
    return {
        runExclusive(name, task) {
            const previous = tails.get(name) ?? Promise.resolve();
            const run = previous.then(() => task());
            const tail = run.then(() => undefined, () => undefined);
            tails.set(name, tail);
            void tail.then(() => {
                if (tails.get(name) === tail)
                    tails.delete(name);
            });
            return run;
        },
    };
}
export const SESSION_CHANGE_CHANNEL = 'vello.session';
/** The `localStorage` key a `WebSecureStore`-backed `SessionStore` writes (prefix `vello.secure.` + `vello.session.v1`). */
export const WEB_SESSION_STORAGE_KEY = 'vello.secure.vello.session.v1';
/**
 * BroadcastChannel when available (not delivered to the sender); otherwise the `storage` event, which browsers fire in
 * every OTHER context when the shared session key changes. Pass `null` to disable either transport explicitly.
 */
export function createSessionChangeSignal(options = {}) {
    const listeners = new Set();
    const emit = () => {
        for (const l of [...listeners]) {
            try {
                l();
            }
            catch {
                /* one faulty listener must not starve the others */
            }
        }
    };
    const Ctor = options.BroadcastChannel === undefined
        ? globalThis.BroadcastChannel
        : (options.BroadcastChannel ?? undefined);
    let channel;
    if (Ctor !== undefined) {
        try {
            channel = new Ctor(options.channelName ?? SESSION_CHANGE_CHANNEL);
            channel.addEventListener('message', emit);
        }
        catch {
            channel = undefined;
        }
    }
    let storageTarget;
    const keys = options.storageKeys ?? [WEB_SESSION_STORAGE_KEY];
    const onStorage = (event) => {
        const key = event.key;
        if (key === null || key === undefined || keys.includes(key))
            emit();
    };
    if (channel === undefined) {
        storageTarget = options.storageTarget === undefined
            ? (globalThis.window ?? undefined)
            : (options.storageTarget ?? undefined);
        storageTarget?.addEventListener('storage', onStorage);
    }
    return {
        notify() {
            try {
                channel?.postMessage({ type: 'session-changed' });
            }
            catch {
                /* best effort — other contexts also re-read storage inside the refresh lock */
            }
        },
        subscribe(listener) {
            listeners.add(listener);
            return () => {
                listeners.delete(listener);
            };
        },
        close() {
            listeners.clear();
            channel?.close();
            storageTarget?.removeEventListener('storage', onStorage);
        },
    };
}
// ---- refresh --------------------------------------------------------------------------------------------
export async function refreshSession(input) {
    const held = input.sessionStore.refreshToken();
    const run = async () => {
        // Re-read the SHARED session inside the lock: another context may have rotated or signed out meanwhile.
        const latest = await input.sessionStore.load();
        const refreshToken = input.sessionStore.refreshToken();
        if (latest.status !== 'authenticated' || refreshToken === undefined) {
            return { ok: false, error: { code: 'UNAUTHORIZED', status: 401 } };
        }
        if (held !== undefined && refreshToken !== held) {
            return { ok: true, value: latest }; // already rotated elsewhere — adopt, never re-present a rotated-away token
        }
        const generation = await input.sessionStore.logoutGeneration();
        if (generation === undefined) {
            // Amendment 4: the shared logout epoch cannot be verified — do not rotate server-side (the successor could not be
            // committed safely). Transient: the watchdog retries; a network-style error keeps the held token.
            return { ok: false, error: { code: 'SERVICE_UNAVAILABLE', status: 503 } };
        }
        const res = await input.auth.refresh({ refreshToken, deviceFingerprint: input.deviceFingerprint });
        if (!res.ok) {
            if (res.error.code === 'UNAUTHORIZED') {
                await input.sessionStore.signOut();
                input.signal?.notify();
            }
            // A network/timeout failure keeps the held token: the server re-issues a lost-response retry (grace window).
            return res;
        }
        // Codex r2 P1-4: a logout that happened while this request was on the network wins — discard the successor rather
        // than resurrect the session (the server session is revoked by that logout; its successors are dead anyway).
        if ((await input.sessionStore.logoutGeneration()) !== generation) {
            await input.sessionStore.load();
            return { ok: false, error: { code: 'UNAUTHORIZED', status: 401 } };
        }
        const state = await input.sessionStore.rotate(res.value);
        input.signal?.notify();
        return { ok: true, value: state };
    };
    return input.lock !== undefined ? input.lock.runExclusive(input.lockName ?? SESSION_REFRESH_LOCK, run) : run();
}
/**
 * Whether an authenticated, ACTIVE session should refresh now: its access token is within `aheadSeconds` of expiry
 * but not yet expired, and the user has been active within `inactivityMs`. An expired token or an idle user is the
 * re-auth path (`requiresReauth`), never a silent renewal.
 */
export function shouldRefreshSession(input) {
    if (input.sessionStore.snapshot().status !== 'authenticated')
        return false;
    const idle = Date.parse(input.now) - Date.parse(input.lastActivityAt);
    if (Number.isNaN(idle) || idle >= input.inactivityMs)
        return false;
    return input.sessionStore.needsRefresh(input.now, input.aheadSeconds) && !input.sessionStore.needsRefresh(input.now, 0);
}
/**
 * A console opened with a session in storage: ask the server one authenticated question (`probe`, usually `auth.me()`).
 * A 401 means the stored access token is stale, so the session is renewed ONCE through the device-bound refresh and the
 * probe asked again; if the server still refuses (or the refresh is refused), the local session is cleared and every tab is
 * told. Only a 401 ends the session: any other failure leaves it alone.
 */
export async function verifyStoredSession(input) {
    const expire = async () => {
        if (input.sessionStore.snapshot().status === 'authenticated') {
            await input.sessionStore.signOut();
            input.signal?.notify();
        }
        return { kind: 'expired' };
    };
    const first = await input.probe();
    if (first.ok)
        return { kind: 'live', value: first.value };
    if (first.error.code !== 'UNAUTHORIZED')
        return { kind: 'unreachable' };
    const renewed = await refreshSession({
        sessionStore: input.sessionStore,
        auth: input.auth,
        deviceFingerprint: input.deviceFingerprint,
        ...(input.lock !== undefined ? { lock: input.lock } : {}),
        ...(input.signal !== undefined ? { signal: input.signal } : {}),
    });
    if (!renewed.ok)
        return renewed.error.code === 'UNAUTHORIZED' ? expire() : { kind: 'unreachable' };
    const second = await input.probe();
    if (second.ok)
        return { kind: 'live', value: second.value };
    return second.error.code === 'UNAUTHORIZED' ? expire() : { kind: 'unreachable' };
}
//# sourceMappingURL=session-refresh.js.map